import { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react'
import { User, Session } from '@supabase/supabase-js'
import { supabase, TENANT_ID } from '@/lib/supabase'
import { useQueryClient } from '@tanstack/react-query'
import type { B2BCustomer, SalespersonIdentity } from '@/types'

interface AuthContextType {
  user: User | null
  session: Session | null
  b2bCustomer: B2BCustomer | null
  salesperson: SalespersonIdentity | null
  isSalesperson: boolean
  isAdmin: boolean
  isLoading: boolean
  authError: string | null
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>
  requestCode: (email: string) => Promise<{ error: Error | null }>
  verifyCode: (email: string, token: string) => Promise<{ error: Error | null }>
  signOut: () => Promise<void>
  refreshCustomer: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [b2bCustomer, setB2bCustomer] = useState<B2BCustomer | null>(null)
  const [isAdmin, setIsAdmin] = useState(false)
  const [salesperson, setSalesperson] = useState<SalespersonIdentity | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const [authError, setAuthError] = useState<string | null>(null)
  const [sessionReady, setSessionReady] = useState(false)
  const [refreshVersion, setRefreshVersion] = useState(0)
  const generation = useRef(0)
  const previousUser = useRef<string | null>(null)
  const loadedUser = useRef<string | null>(null)
  const queryClient = useQueryClient()

  async function refreshCustomer() {
    setRefreshVersion((version) => version + 1)
  }

  useEffect(() => {
    // This callback must remain synchronous: awaiting queries here deadlocks
    // the auth client's session lock. Profile loading runs in the next effect.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, next) => {
      generation.current += 1
      const nextId = next?.user.id ?? null
      if (previousUser.current !== nextId) {
        queryClient.clear()
        setB2bCustomer(null)
        setIsAdmin(false)
        setSalesperson(null)
        previousUser.current = nextId
        loadedUser.current = null
      }
      // Token refreshes and tab focus must not unmount an in-progress checkout.
      setIsLoading(!!nextId && loadedUser.current !== nextId)
      setAuthError(null)
      setSession(next)
      setUser(next?.user ?? null)
      setSessionReady(true)
      setRefreshVersion((version) => version + 1)
    })
    return () => subscription.unsubscribe()
  }, [queryClient])

  useEffect(() => {
    if (!sessionReady) return
    const version = ++generation.current
    if (!session?.user) {
      setIsLoading(false)
      return
    }
    const abort = new AbortController()
    const timer = setTimeout(() => abort.abort(), 15000)
    const hadProfile = loadedUser.current === session.user.id
    setIsLoading(!hadProfile)
    setAuthError(null)

    async function loadProfile() {
      try {
        const [customer, member, seller] = await Promise.all([
          supabase.from('b2b_customers').select('*')
            .eq('user_id', session!.user.id).eq('tenant_id', TENANT_ID)
            .order('status', { ascending: true }).limit(1).abortSignal(abort.signal).maybeSingle(),
          supabase.from('tenant_members').select('role')
            .eq('user_id', session!.user.id).eq('tenant_id', TENANT_ID)
            .abortSignal(abort.signal).maybeSingle(),
          supabase.rpc('b2b_my_salesperson', { p_tenant_id: TENANT_ID }).abortSignal(abort.signal),
        ])
        if (customer.error || member.error || seller.error) throw customer.error || member.error || seller.error
        if (version !== generation.current) return
        setB2bCustomer(customer.data as B2BCustomer | null)
        setIsAdmin(member.data?.role === 'admin')
        setSalesperson(seller.data as unknown as SalespersonIdentity | null)
        loadedUser.current = session!.user.id
      } catch {
        if (version !== generation.current) return
        // Keep the last confirmed profile on a temporary background failure.
        // Database policies still enforce current permissions for every action.
        if (!hadProfile) {
          setB2bCustomer(null)
          setIsAdmin(false)
          setSalesperson(null)
          setAuthError('Não foi possível carregar seu acesso. Tente novamente.')
        }
      } finally {
        clearTimeout(timer)
        if (version === generation.current) setIsLoading(false)
      }
    }
    void loadProfile()
    return () => {
      generation.current += 1
      clearTimeout(timer)
      abort.abort()
    }
  }, [session, sessionReady, refreshVersion])

  async function signIn(email: string, password: string) {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    return { error }
  }

  // Login sem senha: o GoTrue manda um codigo de 6 numeros por e-mail.
  // shouldCreateUser=true porque o primeiro login do lojista cria o auth.user;
  // o banco (trigger b2b_auth_gate) so aceita e-mail ja cadastrado.
  async function requestCode(email: string) {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { shouldCreateUser: true },
    })
    return { error }
  }

  async function verifyCode(email: string, token: string) {
    const { error } = await supabase.auth.verifyOtp({ email, token, type: 'email' })
    return { error }
  }

  async function signOut() {
    await supabase.auth.signOut()
    setB2bCustomer(null)
    setIsAdmin(false)
    setSalesperson(null)
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        b2bCustomer,
        isAdmin,
        salesperson,
        isSalesperson: !!salesperson,
        isLoading,
        authError,
        signIn,
        requestCode,
        verifyCode,
        signOut,
        refreshCustomer,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
